import { useState, useEffect } from 'react';
import { useAuthToken } from '../../../hooks/useAuthToken';
import { usePermissions } from '../../../hooks/usePermissions';
import { supabaseApi } from '../../../lib/api/supabase';
import { SupabaseStatus } from '@optidev/shared-types';
import { Info, Cloud, Database, Lock, Code, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { InsufficientBalanceModal } from '@/components/billing/InsufficientBalanceModal';
import toast from 'react-hot-toast';

import { REGION_CONFIG } from '@/lib/constants/regions';

interface ActivationPanelProps {
  projectId: string;
  onActivated: () => void;
  initialStatus?: SupabaseStatus | null;
}

export function ActivationPanel({ projectId, onActivated, initialStatus }: ActivationPanelProps) {
  const { token } = useAuthToken();
  const { isAdminOrOwner } = usePermissions();
  const [regions, setRegions] = useState<string[]>([]);
  const [selectedRegion, setSelectedRegion] = useState('us-east-2');
  const [activating, setActivating] = useState(initialStatus === SupabaseStatus.PROVISIONING);
  const [progress, setProgress] = useState(initialStatus === SupabaseStatus.PROVISIONING ? 50 : 0);
  const [loadingRegions, setLoadingRegions] = useState(true);
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);

  useEffect(() => {
    if (token) {
      loadRegions();
    }
  }, [token]);

  // Resume polling if already provisioning
  useEffect(() => {
    if (initialStatus === SupabaseStatus.PROVISIONING && token) {
      startProvisioning();
    }
  }, [initialStatus, token]);

  const loadRegions = async () => {
    if (!token) return;

    try {
      const data = await supabaseApi.getRegions(token);
      setRegions(data);
      // Default to us-east-2 if available
      if (!data.includes(selectedRegion) && data.length > 0) {
        setSelectedRegion(data.includes('us-east-2') ? 'us-east-2' : data[0]);
      }
    } catch (error) {
      console.error('Failed to load regions:', error);
      // Use fallback regions with all major ones
      const fallbackRegions = Object.keys(REGION_CONFIG);
      setRegions(fallbackRegions);
      toast.error('Failed to load regions, using defaults');
    } finally {
      setLoadingRegions(false);
    }
  };

  const getSelectedRegionInfo = () => {
    return REGION_CONFIG[selectedRegion] || { id: selectedRegion, name: selectedRegion, flag: '🌐' };
  };

  const startProvisioning = () => {
    if (!token) return;

    // Simulate progress (actual polling happens in backend)
    const progressInterval = setInterval(() => {
      setProgress(p => Math.min(p + 2, 90));
    }, 1000);

    // Poll for completion (provisioning state)
    const pollInterval = setInterval(async () => {
      try {
        const status = await supabaseApi.getProjectStatus(token, projectId);

        if (status.status === SupabaseStatus.ACTIVE_HEALTHY) {
          setProgress(100);
          clearInterval(pollInterval);
          clearInterval(progressInterval);
          toast.success('OptiDev Cloud activated successfully!');
          setTimeout(() => onActivated(), 500);
        } else if (status.status === SupabaseStatus.FAILED) {
          clearInterval(pollInterval);
          clearInterval(progressInterval);
          toast.error('Activation failed. Please try again.');
          setActivating(false);
          setProgress(0);
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 3000); // Poll every 3 seconds

    // Timeout after 3 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      clearInterval(progressInterval);
      if (progress < 100) {
        toast.error('Activation timeout. Please check status and try again.');
        setActivating(false);
        setProgress(0);
      }
    }, 180000);
  };

  const handleActivate = async () => {
    if (!token) return;

    setActivating(true);
    setProgress(0);

    try {
      const response = await supabaseApi.activateProject(token, projectId, selectedRegion);

      // If already active, complete immediately
      if (response.config.status === SupabaseStatus.ACTIVE_HEALTHY) {
        setProgress(100);
        toast.success('OptiDev Cloud activated successfully!');
        setTimeout(() => onActivated(), 500);
        return;
      }

      // Start provisioning polling
      startProvisioning();

    } catch (error: any) {
      console.error('Activation error:', error);

      // Handle 402 Payment Required - insufficient balance
      if (error.response?.status === 402) {
        setShowInsufficientBalanceModal(true);
        setActivating(false);
        setProgress(0);
        return;
      }

      toast.error(error.response?.data?.message || 'Failed to activate OptiDev Cloud');
      setActivating(false);
      setProgress(0);
    }
  };

  if (loadingRegions) {
    return (
      <Card className="max-w-[43rem]">
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
            <span className="ml-3 text-sm text-muted-foreground">Loading regions...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-[43rem]">
      <CardContent className="p-8">
        <div className="flex items-start gap-3 mb-5">
          <div className="flex-shrink-0 w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
            <Cloud className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold mb-1">Activate OptiDev Cloud</h2>
            <p className="text-sm text-muted-foreground">
              Get a fully managed backend for your project with powerful features
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Database className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-medium">PostgreSQL Database</h3>
              </div>
              <p className="text-xs text-muted-foreground">Real-time subscriptions included</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Lock className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-medium">Authentication</h3>
              </div>
              <p className="text-xs text-muted-foreground">Complete user management</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Code className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-medium">Edge Functions</h3>
              </div>
              <p className="text-xs text-muted-foreground">Serverless JavaScript APIs</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-medium">Auto REST API</h3>
              </div>
              <p className="text-xs text-muted-foreground">Generated from your schema</p>
            </CardContent>
          </Card>
        </div>

        {!activating ? (
          <>
            <div className="mb-5">
              <Label htmlFor="region-select" className="block text-sm font-medium mb-2">
                Select Region
              </Label>
              <Select value={selectedRegion} onValueChange={setSelectedRegion} disabled={!isAdminOrOwner}>
                <SelectTrigger id="region-select">
                  <SelectValue>
                    <span className="flex items-center gap-2">
                      <span>{getSelectedRegionInfo().flag} {getSelectedRegionInfo().name}</span>
                      <span className="font-mono text-xs text-muted-foreground">{getSelectedRegionInfo().id}</span>
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {regions.map(r => {
                    const regionInfo = REGION_CONFIG[r] || { id: r, name: r, flag: '🌐' };
                    return (
                      <SelectItem key={r} value={r}>
                        <span className="flex items-center gap-2">
                          <span>{regionInfo.flag} {regionInfo.name}</span>
                          <span className="font-mono text-xs text-muted-foreground">{regionInfo.id}</span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5">
                <Info className="w-3 h-3" />
                Choose a region close to your users for optimal performance
              </p>
            </div>

            <Button
              onClick={handleActivate}
              className="w-full"
              disabled={!isAdminOrOwner}
            >
              Activate OptiDev Cloud
            </Button>
          </>
        ) : (
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-sm font-medium">Setting up your backend...</span>
                <span className="text-sm font-semibold text-primary">{progress}%</span>
              </div>
              <Progress value={progress} className="mb-3" />
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
                <span>This usually takes 1-2 minutes. Please wait...</span>
              </div>
            </CardContent>
          </Card>
        )}
      </CardContent>

      {/* Insufficient Balance Modal */}
      <InsufficientBalanceModal
        isOpen={showInsufficientBalanceModal}
        onClose={() => setShowInsufficientBalanceModal(false)}
        context="cloud"
      />
    </Card>
  );
}
